import { Set } from 'immutable';
import { OG_META_DATA_KEY } from '@opengov/reporting-types';
import isBlank from '@sedan-utils/is-blank';
import Big from 'big.js';

import { nonGraphableKeys } from './constants';
import { IChartData } from './types';

// from data-viz which is not fully isomorphic yet
enum SortOrder {
  numericAscending,
  numericDescending,
  alphaNumericAscending,
  alphaNumericDescending
}

interface ICategoryValue {
  category: string;
  value: Big;
}

export const keysOf = <T>(obj: T): (keyof T)[] => Object.keys(obj) as any[];

export const getCategoriesFromChartData = (data: IChartData[]): Set<string> =>
  // get unique categories from each row of data
  data
    .reduce((categoriesSet, row) => {
      const keys = keysOf(row);
      if (row[OG_META_DATA_KEY]) {
        // we have to look deeper for "show as %" because keys can drop out
        // due to how that query works
        const metaDataKeyPercentTotal = row[OG_META_DATA_KEY].percentTotal;
        const moreKeys = metaDataKeyPercentTotal
          ? Set<string>(keysOf(metaDataKeyPercentTotal).map(String))
          : Set<string>();

        return categoriesSet.merge(keys).merge(moreKeys);
      }
      return categoriesSet.merge(keys);
    }, Set<string>())
    .filter((key: string) => !nonGraphableKeys.includes(key));

export const getCategoriesFromV1EmbedData = (data: any[]): Set<string> => {
  // get unique categories from each row of data
  return data
    .reduce((categoriesSet, row) => {
      const keys = keysOf(row.data);
      return categoriesSet.merge(keys);
    }, Set<string>())
    .filter((key: string) => !nonGraphableKeys.includes(key));
};

// sortedCategoriesForColorScale is the Descending sort for color consistency through the color Scale
// this has to be sorted the same ever time or else the categories will get different colors
// sortedCategories is sorted by the given sortOrder
export const sortCategories = (
  data,
  sortOrder: SortOrder,
  showAsPercentOfTotal: boolean
): { sortedCategoriesForColorScale: string[]; sortedCategories: string[] } => {
  const keySouth = (acc, d) => (key) => {
    if (nonGraphableKeys.includes(key)) {
      return acc;
    }

    const value = d[key];
    if (!isBlank(value)) {
      if (acc[key]) {
        acc[key] = acc[key].plus(d[key]);
      } else {
        acc[key] = Big(d[key]);
      }
    }
  };

  // d3 sorts per column but we want sorting of categories across all columns
  const catToTotal = data.reduce((acc, d) => {
    Object.keys(d).forEach(keySouth(acc, d));

    // could opto this out via a flag passed in
    if (d[OG_META_DATA_KEY] && !showAsPercentOfTotal) {
      Object.keys(d[OG_META_DATA_KEY].value).forEach(keySouth(acc, d[OG_META_DATA_KEY].value));
    }

    return acc;
  }, {});

  const arrayOfCatToTotal: ICategoryValue[] = Object.keys(catToTotal).reduce((acc, cat) => {
    acc.push({ category: cat, value: catToTotal[cat] });
    return acc;
  }, []);

  // this
  const sortedCategoriesForColorScale = arrayOfCatToTotal.sort((a, b) => b.value.cmp(a.value)).map((c) => c.category);
  let sortFn;

  // tslint:disable-next-line:switch-default
  switch (sortOrder) {
    case SortOrder.numericDescending:
      sortFn = (a: ICategoryValue, b: ICategoryValue) => a.value.cmp(b.value);
      break;
    case SortOrder.numericAscending:
      sortFn = (a: ICategoryValue, b: ICategoryValue) => b.value.cmp(a.value);
      break;
    case SortOrder.alphaNumericDescending:
      sortFn = (a: ICategoryValue, b: ICategoryValue) => {
        if (a.category > b.category) {
          return -1;
        }
        if (a.category < b.category) {
          return 1;
        }

        // names must be equal
        return 0;
      };
      break;
    case SortOrder.alphaNumericAscending:
      sortFn = (a: ICategoryValue, b: ICategoryValue) => {
        if (a.category < b.category) {
          return -1;
        }
        if (a.category > b.category) {
          return 1;
        }

        // names must be equal
        return 0;
      };
  }

  const sortedCategories = arrayOfCatToTotal.sort(sortFn).map((c) => c.category);

  return {
    sortedCategoriesForColorScale,
    sortedCategories
  };
};
